import { Send } from 'lucide-react'
import { cn } from '@/lib/utils'

interface PublishedVersionBadgeProps {
  version: number | null | undefined
  publishedAt: string | null | undefined
  className?: string
}

const formatRelative = (iso: string): string => {
  const then = new Date(iso).getTime()
  const now = Date.now()
  const seconds = Math.max(0, Math.round((now - then) / 1000))
  if (seconds < 60) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 14) return `${days}d ago`
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * Phase 14 — small "Published v3 · 2d ago" chip rendered next to the project title in editor +
 * viewer + doing-view headers. Hidden when the project has never been published.
 */
export const PublishedVersionBadge = ({
  version,
  publishedAt,
  className,
}: PublishedVersionBadgeProps) => {
  if (version == null) return null
  const relative = publishedAt ? formatRelative(publishedAt) : null
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground',
        className
      )}
      title={publishedAt ? `Published ${new Date(publishedAt).toLocaleString()}` : undefined}
    >
      <Send className="h-3 w-3" aria-hidden />
      <span>Published v{version}</span>
      {relative && <span className="text-muted-foreground/70">· {relative}</span>}
    </span>
  )
}
